import { api } from '@common';
import { useQuery, useMutation } from '@tanstack/react-query';
import { message } from 'antd';

// Interface for Order Item
export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  sku?: string;
  quantity: number;
  price: number;
  subtotal: number;
  createdAt: Date;
}

// Interface for Order
export interface Order {
  id: string;
  orderNumber: string;
  customerEmail: string;
  status: string;
  totalAmount: number;
  subtotalBeforeDiscount: number;
  discountAmount: number;
  currency: string;
  createdAt: Date;
  updatedAt: Date;
  items: OrderItem[];
}

// Interface for creating checkout session
interface LineItem {
  productId?: string; // Optional - for existing products
  name?: string; // For custom products
  description?: string; // For custom products
  price?: number; // For custom products (in dollars)
  quantity: number;
}

interface CreateCheckoutSessionInput {
  // Legacy single product support
  productId?: string;
  quantity?: number;
  // New flexible line items support
  lineItems?: LineItem[];
  email?: string;
  successUrl?: string;
  cancelUrl?: string;
}

interface CheckoutSessionResponse {
  url: string;
}

interface UseOrdersOptions {
  enableOrdersFetch?: boolean;
}

export const useOrders = (options?: UseOrdersOptions) => {
  const { enableOrdersFetch = false } = options || {};
  
  // Fetch all orders for authenticated user
  const orders = useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: async () => {
      try {
        const response = await api.get('/orders');
        return response.data;
      } catch (error) {
        console.error('Error fetching orders:', error);
        message.error('Error loading orders');
        throw error;
      }
    },
    enabled: enableOrdersFetch && !!localStorage.getItem('token'),
  });

  // Fetch a single order by ID
  const getOrder = (orderId: string, accessToken?: string) => {
    return useQuery<Order>({
      queryKey: ['order', orderId, accessToken],
      queryFn: async () => {
        try {
          const params = accessToken ? { accessToken } : {};
          const response = await api.get(`/orders/${orderId}`, { params });
          return response.data;
        } catch (error) {
          console.error(`Error fetching order ${orderId}:`, error);
          message.error('Error loading order details');
          throw error;
        }
      },
      enabled: !!orderId,
    });
  };

  // Fetch order by Stripe session ID (for order confirmation page)
  const getOrderBySessionId = (sessionId: string) => {
    return useQuery<Order>({
      queryKey: ['order', 'session', sessionId],
      queryFn: async () => {
        try {
          const response = await api.get(`/orders/session/${sessionId}`);
          return response.data;
        } catch (error) {
          console.error(`Error fetching order by session ${sessionId}:`, error);
          throw error;
        }
      },
      enabled: !!sessionId,
      retry: 3,
      retryDelay: 1000,
    });
  };

  // Create a checkout session for payment
  const createCheckoutSession = useMutation({
    mutationFn: async (data: CreateCheckoutSessionInput): Promise<CheckoutSessionResponse> => {
      try {
        const response = await api.post('/payments/create-checkout-session', data);
        return response.data;
      } catch (error: any) {
        console.error('Error creating checkout session:', error);
        
        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to create checkout session: ${errorMsg}`);
        } else {
          message.error('Unable to create checkout session');
        }
        
        throw error;
      }
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    }
  });

  // Create a portal session for subscription management
  const createPortalSession = useMutation({
    mutationFn: async (): Promise<CheckoutSessionResponse> => {
      try {
        const response = await api.post('/payments/create-portal-session');
        return response.data;
      } catch (error) {
        console.error('Error creating portal session:', error);
        message.error('Unable to create portal session');
        throw error;
      }
    },
    onSuccess: (data) => {
      // Redirect to Stripe portal
      if (data.url) {
        window.location.href = data.url;
      }
    }
  });

  return {
    orders,
    getOrder,
    getOrderBySessionId,
    createCheckoutSession,
    createPortalSession,
  };
};
